import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { TodoResponse } from '@/api/types/todo'
import { useTodoStore } from '@/store/useTodoStore'

import { getScheduledMinutes } from './parking-tray-utils'
import { rescheduleTodo } from './schedule-actions'

/** 保留トレイの状態。トレイに置いた時点では API を呼ばないので、リロードすると元の位置に戻る */
export const useParkedTodos = () => {
  const todos = useTodoStore((s) => s.todos)
  const [parkedIds, setParkedIds] = useState<number[]>([])

  const parkedTodos = useMemo(
    () => parkedIds.map((id) => todos.find((t) => t.id === id)).filter((t): t is TodoResponse => t !== undefined),
    [parkedIds, todos],
  )

  const park = useCallback((todoId: number) => {
    setParkedIds((prev) => (prev.includes(todoId) ? prev : [...prev, todoId]))
  }, [])

  const unpark = useCallback((todoId: number) => {
    setParkedIds((prev) => prev.filter((id) => id !== todoId))
  }, [])

  /** 保留中の TODO を start から元の所要時間で配置する */
  const place = useCallback(
    async (todoId: number, start: Date) => {
      const todo = todos.find((t) => t.id === todoId)
      if (!todo) {
        toast.error('対象の TODO が見つかりません')
        return
      }
      const end = new Date(start.getTime() + getScheduledMinutes(todo) * 60000)
      if (await rescheduleTodo(todoId, start, end)) unpark(todoId)
    },
    [todos, unpark],
  )

  return { parkedIds, parkedTodos, park, unpark, place }
}

export type ParkedTodosState = ReturnType<typeof useParkedTodos>
